import type { Provenance, Severity } from "../api/types";

// One source of truth for every color a graph draws.
//
// Rules:
// - Severity owns saturated color. A node is filled with a severity color ONLY
//   when it carries a confirmed finding.
// - Communities get low-chroma, Graphify-like tones, so they read as context and
//   can never be mistaken for a severity or for the VisionHammer orange brand.
// - Only the largest communities get a tone; the long tail stays neutral, so a
//   color always means "this area" rather than one of dozens of near-duplicates.

export const SEVERITY_COLOR: Record<Severity, string> = {
  critical: "#ff5d63",
  high: "#ff9f45",
  medium: "#ffd25a",
  low: "#49cf86",
  info: "#5aa9ff",
};

export const SEVERITY_ORDER: Severity[] = ["critical", "high", "medium", "low", "info"];

// Muted community tones (OKLCH ~L0.70 / C0.05), spread around the wheel.
export const COMMUNITY_TONES = [
  "#8e88b8", // slate violet
  "#6f9e9a", // dusty teal
  "#93a47f", // sage
  "#b5a382", // sand
  "#a9879f", // mauve
  "#7f93ad", // steel
  "#ad8d84", // clay
  "#9c9f76", // lichen
];

export const NEUTRAL_NODE = "#5f5b58";

export const PROVENANCE_COLOR: Record<Provenance, string> = {
  extracted: "#a39d97",
  inferred: "#77716c",
  ambiguous: "#8f6a6f",
};

/** Community -> tone for the largest communities; everything else is neutral. */
export function communityTones(communities: (number | null | undefined)[], max = COMMUNITY_TONES.length) {
  const size = new Map<number, number>();
  for (const c of communities) {
    if (c === null || c === undefined) continue;
    size.set(c, (size.get(c) ?? 0) + 1);
  }
  const ranked = [...size.entries()].filter(([, n]) => n > 1).sort((a, b) => b[1] - a[1] || a[0] - b[0]);
  const tone = new Map<number, string>();
  ranked.slice(0, max).forEach(([c], i) => tone.set(c, COMMUNITY_TONES[i]));
  return (community: number | null | undefined) =>
    community === null || community === undefined ? NEUTRAL_NODE : tone.get(community) ?? NEUTRAL_NODE;
}
